import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { api } from '@/lib/api';
import { displayGroupName } from '@/lib/group-display';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

interface GroupAffiliation {
  id: string;
  name: string;
  type: 'manual' | 'template';
  category: 'committee' | 'group';
  isActive: boolean;
  isLeader: boolean;
  roles: string[];
}

interface DepartmentAffiliation {
  id: string;
  name: string;
  roles: string[];
}

interface UnitAffiliation {
  id: string;
  name: string;
  departmentId: string;
  departmentName: string;
  roles: string[];
}

interface CouncilOffice {
  id: string;
  roleName: string;
  roleCode: string | null;
  termId: string;
  termLabel: string | null;
  startYear: number;
  endYear: number | null;
}

interface LeadershipPost {
  id: string;
  kind: 'group' | 'committee' | 'department' | 'unit' | 'minister';
  name: string;
  roles: string[];
  groupId: string | null;
  departmentId: string | null;
}

export interface MemberInvolvement {
  groups: GroupAffiliation[];
  committees: GroupAffiliation[];
  departments: DepartmentAffiliation[];
  units: UnitAffiliation[];
  council: CouncilOffice[];
  leadership: LeadershipPost[];
}

const leadershipPath = (
  row: LeadershipPost,
  canViewGroups: boolean,
): string | undefined => {
  if (row.kind === 'group' && row.groupId && canViewGroups) return `/groups/${row.groupId}`;
  if (row.kind === 'committee' && row.groupId && canViewGroups) {
    return `/committees/${row.groupId}`;
  }
  if ((row.kind === 'department' || row.kind === 'unit') && row.departmentId) {
    return `/people/departments/${row.departmentId}`;
  }
  if (row.kind === 'minister') return '/people/leadership/ministers';
  return undefined;
};

const uniqueRoles = (roles: string[]) => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const role of roles) {
    const key = role.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(role);
  }
  return result;
};

const InvolvementRow = ({
  label,
  empty,
  items,
}: {
  label: string;
  empty: string;
  items: Array<{ key: string; to?: string; name: string; roles: string[] }>;
}) => (
  <div className="grid grid-cols-1 gap-0.5 text-body-sm sm:grid-cols-[10rem_1fr] sm:gap-2">
    <dt className="text-fg-secondary">{label}</dt>
    <dd className="min-w-0">
      {items.length === 0 ? (
        <span>{empty}</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {items.map((item) => (
            <li key={item.key} className="min-w-0 break-words">
              {item.to ? (
                <Link to={item.to} className="font-medium text-brand hover:underline">
                  {item.name}
                </Link>
              ) : (
                <span className="font-medium">{item.name}</span>
              )}
              {item.roles.length > 0 ? (
                <span className="text-fg-secondary"> · {item.roles.join(', ')}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </dd>
  </div>
);

export const MemberInvolvementCard = ({ memberId }: { memberId: string }) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tGroups } = useTranslation('groups');
  const membership = useChurchStore((state) => state.membership);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const canViewGroups =
    isModuleEnabled('groups') && hasPermission(membership, PERMISSIONS.GROUPS_VIEW);

  const involvement = useQuery({
    queryKey: ['people', 'member', memberId, 'involvement'],
    queryFn: () => api.get<MemberInvolvement>(`/people/members/${memberId}/involvement`),
    enabled: Boolean(memberId),
  });

  const data = involvement.data;
  const toGroupItem = (row: GroupAffiliation) => ({
    key: row.id,
    to: canViewGroups ? `/groups/${row.id}` : undefined,
    name: displayGroupName(row),
    roles: uniqueRoles([
      ...(row.isLeader ? [tGroups('leader')] : []),
      ...row.roles,
    ]),
  });
  const manualGroupItems = (data?.groups ?? [])
    .filter((row) => row.type === 'manual')
    .map(toGroupItem);
  const smartGroupItems = (data?.groups ?? [])
    .filter((row) => row.type === 'template')
    .map(toGroupItem);
  const committeeItems = (data?.committees ?? []).map((row) => ({
    key: row.id,
    to: canViewGroups ? `/committees/${row.id}` : undefined,
    name: row.name,
    roles: uniqueRoles([
      ...(row.isLeader ? [tGroups('leader')] : []),
      ...row.roles,
    ]),
  }));
  const departmentItems = (data?.departments ?? []).map((row) => ({
    key: row.id,
    to: `/people/departments/${row.id}`,
    name: row.name,
    roles: uniqueRoles(row.roles),
  }));
  const unitItems = (data?.units ?? []).map((row) => ({
    key: row.id,
    to: `/people/departments/${row.departmentId}`,
    name: t('members.detail.involvement.unitInDepartment', {
      unit: row.name,
      department: row.departmentName,
    }),
    roles: row.roles,
  }));
  const councilItems = (data?.council ?? []).map((row) => ({
    key: row.id,
    to: '/people/leadership/council',
    name: row.roleName,
    roles: [] as string[],
  }));
  const leadershipItems = (data?.leadership ?? []).map((row) => {
    const roles =
      (row.kind === 'group' || row.kind === 'committee') && row.roles.length === 0
        ? [tGroups('leader')]
        : uniqueRoles(row.roles);
    return {
      key: row.id,
      to: leadershipPath(row, canViewGroups),
      name: row.name,
      roles,
    };
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('members.detail.sections.involvement')}</CardTitle>
      </CardHeader>
      <CardContent>
        {involvement.isLoading ? (
          <div className="flex min-h-16 items-center justify-center">
            <Spinner className="size-6 text-brand" />
            <span className="sr-only">{tCommon('state.loading')}</span>
          </div>
        ) : null}
        {involvement.isError ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-body-sm text-fg-secondary">
              {t('members.detail.involvement.loadError')}
            </p>
            <Button size="sm" variant="outline" onClick={() => void involvement.refetch()}>
              {tCommon('action.retry')}
            </Button>
          </div>
        ) : null}
        {!involvement.isLoading && !involvement.isError ? (
          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {isModuleEnabled('groups') ? (
              <>
                <InvolvementRow
                  label={t('members.detail.involvement.manualGroups')}
                  empty={t('members.detail.involvement.emptyManualGroups')}
                  items={manualGroupItems}
                />
                <InvolvementRow
                  label={t('members.detail.involvement.smartGroups')}
                  empty={t('members.detail.involvement.emptySmartGroups')}
                  items={smartGroupItems}
                />
                <InvolvementRow
                  label={t('members.detail.involvement.committees')}
                  empty={t('members.detail.involvement.emptyCommittees')}
                  items={committeeItems}
                />
              </>
            ) : null}
            <InvolvementRow
              label={t('members.detail.involvement.departments')}
              empty={t('members.detail.involvement.emptyDepartments')}
              items={departmentItems}
            />
            <InvolvementRow
              label={t('members.detail.involvement.units')}
              empty={t('members.detail.involvement.emptyUnits')}
              items={unitItems}
            />
            <InvolvementRow
              label={t('members.detail.involvement.council')}
              empty={t('members.detail.involvement.emptyCouncil')}
              items={councilItems}
            />
            <InvolvementRow
              label={t('members.detail.involvement.leadership')}
              empty={t('members.detail.involvement.emptyLeadership')}
              items={leadershipItems}
            />
          </dl>
        ) : null}
      </CardContent>
    </Card>
  );
};
